import {isEmpty} from 'es-toolkit/compat';
import {inject, injectable} from 'inversify';

import type {BNBoundFormApiModel} from '../../../../../../../api/types/bnrks/models';
import type {CoreBoundFormApiModel} from '../../../../../../../api/types/core/models';
import type {IApiDataMapper} from '../../../../../../../core/ports/types';
import type {Combine} from '../../../../../../../utils/utility-types';

import {AnswerMapperJrpc} from '../../../../../../../core/domain/Answer/mappers/jrpc/Answer.mapper.jrpc';
import {QuestionMapperJrpc} from '../../../../../../../core/domain/Question/mappers/jrpc/Question.mapper.jrpc';
import {UserMapperJrpc} from '../../../../../../../core/domain/User/mappers/User.mapper.jrpc';
import {CromModel} from '../../Crom.model';
import {CromModelFactory} from '../../CromModel.factory';
import {CromExpandJrpc} from './Crom.expand.jrpc';

@injectable()
export class CromMapperJrpc
  implements
    IApiDataMapper<CromMapperJrpc.ExpandedApiData, CromModel.Props, CromModel>
{
  constructor(
    @inject(UserMapperJrpc)
    private readonly userMapper: UserMapperJrpc,
    @inject(CromModelFactory)
    private readonly cromModelFactory: CromModelFactory,
    @inject(QuestionMapperJrpc)
    private readonly questionMapper: QuestionMapperJrpc,
    @inject(AnswerMapperJrpc)
    private readonly answerMapper: AnswerMapperJrpc,
    @inject(CromExpandJrpc)
    readonly expand: CromExpandJrpc,
  ) {}

  mapDataToModel(data: CromMapperJrpc.ExpandedApiData) {
    return this.cromModelFactory.create(this.mapDataToProps(data));
  }

  mapDataToProps(data: CromMapperJrpc.ExpandedApiData): CromModel.Props {
    const questions = this.questions(data);
    const answers = isEmpty(data.answers)
      ? null
      : this.answerMapper.mapAnswersToModelsByQuestionId(
          data.answers,
          questions,
        );

    return {
      answers,
      client: this.userMapper.mapDataToModelOrEmpty(data.user),
      id: data._id,
      isAiGenerated: data.ai_generated ?? false,
      questions,
    };
  }

  private questions({form}: CromMapperJrpc.ExpandedApiData) {
    return form ? this.questionMapper.mapSectionsToModels(form?.sections) : [];
  }
}

export namespace CromMapperJrpc {
  export type ApiData = Combine<CoreBoundFormApiModel, BNBoundFormApiModel>;

  export type ExpandedApiData = ApiData & CromExpandJrpc.ExpandedFields;
}
